import React, { FC, ReactNode, useId, useRef } from 'react';
import styled from 'styled-components';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import { ClickAwayListener, Popper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FilterModel } from '../../../common/model/ui/filter.model';
import {
  getFilterLabel,
  getFilterValueLabel,
  isFilterApplied,
} from '../../../common/utils/services/list-filter-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvListFilterEditor from './OvListFilterEditor';

interface OvListFilterControlProps {
  filter: FilterModel;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onApply: (value: string) => void;
  footer?: ReactNode;
}

// The filter rail scrolls sideways on phones and would clip an absolute popper.
const POPPER_OPTIONS = { strategy: 'fixed' } as const;

const POPPER_MODIFIERS = [
  { name: 'offset', options: { offset: [0, 8] } },
  { name: 'preventOverflow', options: { padding: 16 } },
];

const OvListFilterControl: FC<OvListFilterControlProps> = ({
  filter,
  isOpen,
  onToggle,
  onClose,
  onApply,
  footer = null,
}) => {
  const { t } = useTranslation();
  const popoverId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const label = getFilterLabel(filter, t);
  const isApplied = isFilterApplied(filter);
  const hasValueLabel = isApplied || filter.defaultValue !== undefined;
  const valueLabel = isApplied
    ? getFilterValueLabel(filter, t)
    : getFilterValueLabel(filter, t, filter.defaultValue);
  const accessibleLabel = hasValueLabel ? `${label}: ${valueLabel}` : label;

  const applyAndRestoreFocus = (value: string) => {
    onApply(value);
    triggerRef.current?.focus();
  };

  const cancelAndRestoreFocus = () => {
    onClose();
    triggerRef.current?.focus();
  };

  return (
    <ClickAwayListener onClickAway={() => isOpen && onClose()}>
      <Control $isApplied={isApplied}>
        <Trigger
          ref={triggerRef}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-controls={isOpen ? popoverId : undefined}
          aria-label={accessibleLabel}
          title={accessibleLabel}
          $isApplied={isApplied}
          onClick={onToggle}
        >
          <span>{hasValueLabel ? `${label}:` : label}</span>
          {hasValueLabel && <Value $isApplied={isApplied}>{valueLabel}</Value>}
          <ArrowDropDownIcon aria-hidden="true" />
        </Trigger>
        {isApplied && (
          <Remove
            type="button"
            aria-label={t('common.listFilters.remove', { name: label })}
            onClick={() => applyAndRestoreFocus('')}
          >
            <CloseIcon aria-hidden="true" />
          </Remove>
        )}
        <StyledPopper
          id={popoverId}
          open={isOpen}
          anchorEl={triggerRef.current}
          placement="bottom-start"
          modifiers={POPPER_MODIFIERS}
          popperOptions={POPPER_OPTIONS}
          disablePortal
        >
          <OvListFilterEditor
            filter={filter}
            onApply={applyAndRestoreFocus}
            onCancel={cancelAndRestoreFocus}
            footer={footer}
          />
        </StyledPopper>
      </Control>
    </ClickAwayListener>
  );
};

export default OvListFilterControl;

const Control = styled.div<{ $isApplied: boolean }>`
  display: inline-flex;
  align-items: stretch;
  max-width: 100%;
  border: 1px solid
    ${({ $isApplied }) =>
      $isApplied ? Colours.OV_ACCENT_BLUE : Colours.OV_BORDER_COLOR_V3};
  border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
  background-color: ${({ $isApplied }) =>
    $isApplied ? Colours.OV_LIGHT_BLUE : Colours.WHITE};
`;

const Trigger = styled.button<{ $isApplied: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  max-width: 100%;
  min-height: 2.125rem;
  padding: ${({ $isApplied }) =>
    $isApplied
      ? '0.3125rem 0.4375rem 0.3125rem 0.6875rem'
      : '0.3125rem 0.6875rem'};
  border: 0;
  border-radius: ${({ $isApplied }) =>
    $isApplied
      ? `${Variables.borderRadius.CLINIC_DASHBOARD_LARGE} 0 0 ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE}`
      : Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
  background: transparent;
  color: ${Colours.OV_BASE};
  font: inherit;
  font-size: ${Variables.fontSizes.MEDIUM};
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background-color: ${Colours.OV_WHITE_HOVER};
  }

  &:focus-visible {
    outline: 3px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: 2px;
  }

  .MuiSvgIcon-root {
    flex: none;
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const Value = styled.span<{ $isApplied: boolean }>`
  display: inline-block;
  max-width: 10.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: ${({ $isApplied }) => ($isApplied ? 'bold' : 'normal')};
`;

const Remove = styled.button`
  display: inline-flex;
  align-items: center;
  flex: none;
  padding: 0.3125rem 0.4375rem;
  border: 0;
  border-left: 1px solid ${Colours.OV_ACCENT_BLUE};
  border-radius: 0 ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE}
    ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE} 0;
  background: transparent;
  color: ${Colours.OV_BASE};
  cursor: pointer;

  &:hover {
    background-color: ${Colours.OV_WHITE_HOVER};
  }

  &:focus-visible {
    outline: 3px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: 2px;
  }

  .MuiSvgIcon-root {
    width: 0.875rem;
    height: 0.875rem;
  }
`;

const StyledPopper = styled(Popper)`
  z-index: 25;
`;
